import { useEffect } from "react";

export function useDeferredMeetJozPortalAction({
  currentPortal,
  pendingPortalActionRef,
  setPendingMeetJozVoiceAction,
  meetJozVoiceReady,
  pendingMeetJozVoiceAction,
  setMeetJozVoiceReady,
  meetJozVoiceReadyRef,
  forceMeetJozVibeStateRef,
  forceMeetJozDiscoverStateRef,
  forceMeetJozSkillsStateRef,
}) {
  const STALE_INTENT_MS = 8000;

  useEffect(() => {
    if (!pendingPortalActionRef.current) return;
    if (currentPortal !== "meet-joz") return;
    if (!meetJozVoiceReady) return;

    const { action, runNuclearSkillsSequence, requestedAt } = pendingPortalActionRef.current;
    let clearPendingHandle = null;
    let nuclearSkillsHandle = null;
    let discoverSequenceHandle = null;
    const clearPendingAction = () => {
      pendingPortalActionRef.current = null;
      clearPendingHandle = window.setTimeout(() => {
        setPendingMeetJozVoiceAction("");
      }, 900);
    };

    if (requestedAt && Date.now() - requestedAt > STALE_INTENT_MS) {
      console.warn("⚠️ Dropping stale deferred Meet Joz intent:", {
        action,
        requestedAt,
      });
      clearPendingAction();
      return () => {
        if (clearPendingHandle) {
          window.clearTimeout(clearPendingHandle);
        }
      };
    }

    console.log("🌀 Running deferred portal action:", action, "ready");

    if (runNuclearSkillsSequence) {
      const prepared = window.__prepareControlledGLBEntry?.("vibe");
      if (!prepared) {
        console.warn("⚠️ Nuclear Skills sequence failed to prepare staged vibe entry.");
        forceMeetJozSkillsStateRef?.current?.();
        clearPendingAction();
        return () => {
          if (clearPendingHandle) {
            window.clearTimeout(clearPendingHandle);
          }
        };
      }

      forceMeetJozDiscoverStateRef?.current?.();

      nuclearSkillsHandle = window.setTimeout(() => {
        const triggered = window.__triggerControlledGLB?.("skills");

        if (!triggered) {
          console.warn("⚠️ Nuclear Skills sequence failed on staged skills trigger:", {
            prepared,
            triggered,
          });
          forceMeetJozSkillsStateRef?.current?.();
        }

        clearPendingAction();
      }, 120);

      return () => {
        if (nuclearSkillsHandle) {
          window.clearTimeout(nuclearSkillsHandle);
        }
        if (discoverSequenceHandle) {
          window.clearTimeout(discoverSequenceHandle);
        }
        if (clearPendingHandle) {
          window.clearTimeout(clearPendingHandle);
        }
      };
    }

    if (action === "vibe") {
      forceMeetJozVibeStateRef?.current?.();
      clearPendingAction();
      return () => {
        if (clearPendingHandle) {
          window.clearTimeout(clearPendingHandle);
        }
      };
    }

    if (action === "discover") {
      const prepared = window.__prepareControlledGLBEntry?.("discover");
      const triggered = window.__triggerControlledGLB?.("discover");

      if (!prepared || !triggered) {
        console.warn("⚠️ Discover sequence failed after ready signal:", {
          action,
          prepared,
          triggered,
        });
        forceMeetJozDiscoverStateRef?.current?.();
      }

      clearPendingAction();
      return () => {
        if (discoverSequenceHandle) {
          window.clearTimeout(discoverSequenceHandle);
        }
        if (clearPendingHandle) {
          window.clearTimeout(clearPendingHandle);
        }
      };
    }

    if (action === "skills") {
      forceMeetJozSkillsStateRef?.current?.();
      clearPendingAction();
      return () => {
        if (clearPendingHandle) {
          window.clearTimeout(clearPendingHandle);
        }
      };
    }

    return () => {
      if (discoverSequenceHandle) {
        window.clearTimeout(discoverSequenceHandle);
      }
      if (clearPendingHandle) {
        window.clearTimeout(clearPendingHandle);
      }
    };
  }, [
    currentPortal,
    forceMeetJozDiscoverStateRef,
    forceMeetJozSkillsStateRef,
    forceMeetJozVibeStateRef,
    meetJozVoiceReady,
    pendingMeetJozVoiceAction,
    pendingPortalActionRef,
    setPendingMeetJozVoiceAction,
  ]);

  useEffect(() => {
    if (currentPortal === "meet-joz") return;
    if (!pendingMeetJozVoiceAction) return;
    setPendingMeetJozVoiceAction("");
  }, [currentPortal, pendingMeetJozVoiceAction, setPendingMeetJozVoiceAction]);

  useEffect(() => {
    if (currentPortal === "meet-joz") return;
    if (!meetJozVoiceReady) return;
    setMeetJozVoiceReady(false);
  }, [currentPortal, meetJozVoiceReady, setMeetJozVoiceReady]);

  useEffect(() => {
    meetJozVoiceReadyRef.current = meetJozVoiceReady;
  }, [meetJozVoiceReady, meetJozVoiceReadyRef]);
}
